// sync-merge.js — fold downloaded CRM rows into the local learner-store (Brief 025).
//
// Split out of sync-engine.js so the reducer is unit-testable with an injected
// store and no transport at all.
//
// THE ONE INVARIANT: a merge is MONOTONE. It may only move local progress
// FORWARD — never clear a completion, never reduce a streak, never un-master a
// word. The server already does monotone-max per table (Brief 025 §Out of
// scope: "client-side conflict resolution — server does monotone-max"); the
// client must be no more destructive, because a pull can land at ANY moment,
// including mid-exercise, and a learner watching their own progress shrink is
// the worst possible failure mode.
//
// Consequence: there is no "server wins" path here, and a delete on one device
// does not propagate as a delete to another. That is deliberate for V1. The
// one soft-delete we DO carry (`app_global_playlist.to_be_deleted`) is absent
// from the download payload anyway — iOS hard-codes it to 0 on ingest
// (KFESyncUserProgressDataDbMgr.m:705-709) and we mirror that.
//
// Tables intentionally NOT merged, and why:
//   • summary_* telemetry (`words_met`, `words_recognition`, `mmus_met`,
//     `time_spent`, `daily_use`, `multi_choice_*`) — these are append-only
//     REPORTING aggregates on the CRM side with no local counterpart that the
//     web runtime reads back. Ingesting them would fabricate local rows nothing
//     queries. (iOS likewise skips `summary_words_met` + `summary_time_spent`
//     on ingest — KFESyncUserProgressDataDbMgr.m:210-216.)
//   • learner_test_instances / learner_meter_answers — per-attempt history.
//     Web's local equivalents (`sessions`, `meterTelemetry`) are write-only
//     analytics; re-materialising another device's attempts there would
//     double-count nothing useful.
//   • app_review_items / app_TF_combinations — no web consumer yet.
// Each of those is a candidate for a later brief, not a silent gap: the
// `skipped` counter in the result names them so a smoke can see them arrive.

/** Consecutive correct answers that count as "mastered". Mirrors the default
 *  `masteryTarget` on `recordMmuAttempt` / `recordL2bMmuAttempt`. */
const MASTERY_TARGET = 3;

/** Tables this reducer knows how to fold in. Everything else is counted + skipped. */
const MERGED_TABLES = new Set([
  "learner_lesson_items",
  "app_item_info",
  "learner_l2b_mmus",
  "summary_lessons_completed",
  "app_global_playlist",
  "app_progress_variables",
]);

/** @param {unknown} v @returns {number} */
function int(v) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : 0;
}

/**
 * CRM epoch-seconds → client-clock ms. Rows carry seconds (`time_completed`,
 * `at_time`); every local `lastCompleted` / `updatedAt` is ms. Zero stays zero
 * so an absent timestamp doesn't become 1970-in-ms.
 * @param {unknown} v @returns {number}
 */
function secToMs(v) {
  const n = int(v);
  return n > 0 ? n * 1000 : 0;
}

/**
 * Fold parsed download rows into the learner store.
 *
 * @param {Record<string, Array<Record<string, string|null>>>} parsed
 *        output of `progress-rows.parseDownloadedProgress`
 * @param {any} learner the learner-store module (injected for tests)
 * @returns {Promise<{ merged: Record<string, number>, skipped: Record<string, number>, errors: number }>}
 */
export async function mergeDownloadedProgress(parsed, learner) {
  /** @type {Record<string, number>} */ const merged = {};
  /** @type {Record<string, number>} */ const skipped = {};
  let errors = 0;

  for (const [table, rows] of Object.entries(parsed ?? {})) {
    if (!MERGED_TABLES.has(table)) {
      skipped[table] = (skipped[table] ?? 0) + rows.length;
      continue;
    }
    for (const row of rows) {
      try {
        const did = await mergeRow(table, row, learner);
        if (did) merged[table] = (merged[table] ?? 0) + 1;
      } catch (err) {
        errors++;
        console.warn(`[sync-merge] ${table} row failed`, err);
      }
    }
  }
  return { merged, skipped, errors };
}

/**
 * Fold ONE row. Returns whether it changed anything worth counting.
 * @param {string} table
 * @param {Record<string, string|null>} row
 * @param {any} learner
 * @returns {Promise<boolean>}
 */
async function mergeRow(table, row, learner) {
  switch (table) {
    case "learner_lesson_items": {
      // iOS's completion record. `completed` is a COUNT (Brief 072 —
      // KFEDatabaseManager.m:494 `set completed = ++numberOfCompletions`), so
      // monotone-max it against the local `completedCount` rather than OR-ing a
      // flag. A device that has passed the item three times must not be pulled
      // back to one by a device that passed it once.
      const itemId = int(row.item_id);
      if (!itemId) return false;
      const remoteCount = int(row.completed);
      const remoteAt = secToMs(row.time_completed);
      const local = (await learner.getItemProgress(itemId)) ?? {};
      const nextCount = Math.max(int(local.completedCount), remoteCount);
      const nextAt = Math.max(int(local.lastCompleted), remoteAt);
      if (nextCount === int(local.completedCount) && nextAt === int(local.lastCompleted)) return false;
      await learner.updateItemProgress(itemId, { completedCount: nextCount, lastCompleted: nextAt });
      return true;
    }

    case "app_item_info": {
      // Per-item authored/derived metadata. `required_play` and
      // `add_to_global_playlist` are course-designer signals the local store
      // caches (CLAUDE.md Rule #8 — code honours them, never invents them), and
      // `completed` is the same count as above. Only ever widened.
      const itemId = int(row.item_id);
      if (!itemId) return false;
      const local = (await learner.getItemProgress(itemId)) ?? {};
      /** @type {Record<string, number>} */ const patch = {};
      const remoteCount = int(row.completed);
      if (remoteCount > int(local.completedCount)) patch.completedCount = remoteCount;
      const remoteReq = int(row.required_play);
      if (remoteReq > 0 && remoteReq !== int(local.requiredPlay)) patch.requiredPlay = remoteReq;
      if (Object.keys(patch).length === 0) return false;
      await learner.updateItemProgress(itemId, patch);
      return true;
    }

    case "learner_l2b_mmus": {
      // The GLOBAL mmuStreaks row, not the per-exercise `l2bStreaks` one: the
      // CRM row has no item_id (fields are `mmu_id`,
      // `consecutive_correct_answers`), so it cannot be attributed to an
      // exercise. Per Brief 182 the global row is the "has this word been met"
      // signal that L2B Testing / HAAI pools read, which is exactly the
      // cross-device fact worth syncing; per-exercise mastery stays local.
      const mmuId = int(row.mmu_id);
      if (!mmuId) return false;
      const remoteStreak = int(row.consecutive_correct_answers);
      const local = (await learner.getMmuStreak(mmuId)) ?? { mmuId, streak: 0, lastSeen: 0, mastered: false };
      const nextStreak = Math.max(int(local.streak), remoteStreak);
      const nextMastered = !!local.mastered || nextStreak >= MASTERY_TARGET;
      if (nextStreak === int(local.streak) && nextMastered === !!local.mastered) return false;
      await learner.putMmuStreak({
        ...local,
        mmuId,
        streak: nextStreak,
        mastered: nextMastered,
        lastSeen: Math.max(int(local.lastSeen), Date.now()),
      });
      return true;
    }

    case "summary_lessons_completed": {
      // The persistent "lesson_completed already fired" flag. Marking it from a
      // remote row is what stops the Lesson Report re-popping on this device for
      // a lesson the learner already finished on iOS (Brief 095 idempotency).
      const lessonItemId = int(row.item_id);
      if (!lessonItemId) return false;
      const lessonId = String(lessonItemId);
      if (await learner.hasLessonCompletedFired(lessonId)) return false;
      await learner.markLessonCompletedFired(lessonId, secToMs(row.at_time) || Date.now());
      return true;
    }

    case "app_global_playlist": {
      // Additive only. `to_be_deleted` is absent from the download payload (iOS
      // hard-codes 0 on ingest), so a remote REMOVAL never reaches us — and the
      // web's soft-delete is sticky (Brief 090), so re-adding a locally-removed
      // item would fight the learner. `addToGlobalPlaylistManual` is idempotent
      // and honours that stickiness, so it is the right entry point.
      const itemId = int(row.item_id);
      if (!itemId) return false;
      // `getGlobalPlaylistMembership` returns "present" | "removed" | "absent".
      // Only "absent" may be added: "present" is already there, and "removed"
      // is the learner's sticky soft-delete, which an automatic path must never
      // resurrect. (`addToGlobalPlaylistManual` DOES resurrect — that is correct
      // for an explicit tap, wrong here — so the guard is load-bearing.)
      if ((await learner.getGlobalPlaylistMembership(itemId)) !== "absent") return false;
      await learner.addToGlobalPlaylistManual(itemId);
      return true;
    }

    case "app_progress_variables": {
      // The singleton settings row. HOLD-OUT for V1 (2026-09-14 live smoke
      // regression): iOS's `active_pathway` is a numeric plist ID (e.g. "103330")
      // and the web's pathway loader keys off a different string ID
      // (`kfe_zero_to_fluency`). Merging the iOS value blew away the web's
      // active_pathway and every lesson-detail navigation 404'd. A follow-up
      // brief needs to establish an ID mapping (or ratchet the web to iOS's
      // numeric IDs) before this row can safely round-trip. Until then, treat
      // this table as read-only: no-op the merge so the pull path stays useful
      // for the other tables. `personal_promise_signed` /
      // `summary_words_met_last_id_updated` / `username` also have no web
      // consumer today, so nothing else in the row is worth folding in.
      return false;
    }

    default:
      return false;
  }
}
